/**
 * PredictionOverlay — "predict before reveal" overlay (P16.2).
 *
 * Phase 1 (predict): Shows the question and a free-text textarea.
 * The learner writes their prediction and locks it in.
 * Phase 2 (reveal): Shows the question, the learner's prediction,
 * and the correct answer from marker.answer.
 *
 * Transition to reveal occurs when reveal_at_ms passes OR the learner
 * presses "Skip to Reveal" OR after locking in the prediction.
 *
 * Props:
 *   marker   — { question, answer, reveal_at_ms }
 *   sceneId  — string scene ID for event context
 *   onClose  — () => void called to dismiss
 */

import React, { useEffect, useRef, useState } from 'react';
import { emitLearnerEvent } from '../../services/learnerEvents';

export default function PredictionOverlay({ marker, sceneId, onClose }) {
  const [phase, setPhase] = useState('predict');
  const [predictionText, setPredictionText] = useState('');
  const [saving, setSaving] = useState(false);
  const mountTimeRef = useRef(Date.now());
  const emittedRef = useRef(false);

  useEffect(() => {
    if (!marker?.reveal_at_ms) return undefined;
    const delay = marker.reveal_at_ms - Date.now();
    if (delay <= 0) {
      setPhase('reveal');
      return undefined;
    }
    const timer = setTimeout(() => setPhase('reveal'), delay);
    return () => clearTimeout(timer);
  }, [marker?.reveal_at_ms]);

  const emitEvent = async (text) => {
    if (emittedRef.current) return;
    emittedRef.current = true;
    await emitLearnerEvent({
      event_type: 'overlay_answered',
      overlay_type: 'prediction',
      scene_id: sceneId,
      prediction_text: text,
      is_correct: null,
      time_to_answer_ms: Date.now() - mountTimeRef.current,
    });
  };

  const handleLockIn = async () => {
    if (saving) return;
    setSaving(true);
    await emitEvent(predictionText.trim());
    setSaving(false);
    setPhase('reveal');
  };

  const handleSkipToReveal = async () => {
    await emitEvent(predictionText.trim());
    setPhase('reveal');
  };

  const handleClose = () => {
    if (onClose) onClose();
  };

  return (
    <div style={overlayStyle}>
      <div style={cardStyle}>
        <div style={headerStyle}>
          <span style={labelStyle}>
            {phase === 'predict' ? 'Predict Before Reveal' : 'Reveal'}
          </span>
          {phase === 'predict' && (
            <button onClick={handleSkipToReveal} style={skipBtnStyle}>
              Skip to Reveal
            </button>
          )}
        </div>

        <p style={questionStyle}>{marker?.question}</p>

        {phase === 'predict' ? (
          <>
            <textarea
              value={predictionText}
              onChange={(e) => setPredictionText(e.target.value)}
              placeholder="Write your prediction here…"
              rows={4}
              style={textareaStyle}
            />
            <button
              onClick={handleLockIn}
              disabled={saving || !predictionText.trim()}
              style={{
                ...primaryBtnStyle,
                opacity: (saving || !predictionText.trim()) ? 0.5 : 1,
                cursor: (saving || !predictionText.trim()) ? 'default' : 'pointer',
              }}
            >
              {saving ? 'Locking in…' : 'Lock in Prediction'}
            </button>
          </>
        ) : (
          <>
            {predictionText.trim() ? (
              <div style={predictionDisplayStyle}>
                <div style={subLabelStyle}>Your Prediction</div>
                <p style={{ margin: 0, color: '#e2e8f0', fontSize: 14, lineHeight: 1.6 }}>
                  {predictionText}
                </p>
              </div>
            ) : null}
            <div style={answerDisplayStyle}>
              <div style={subLabelStyle}>Correct Answer</div>
              <p style={{ margin: 0, color: '#86efac', fontSize: 14, lineHeight: 1.6 }}>
                {marker?.answer}
              </p>
            </div>
            <button onClick={handleClose} style={primaryBtnStyle}>
              Close
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const overlayStyle = {
  position: 'absolute',
  inset: 0,
  background: 'rgba(0,0,0,0.85)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 50,
  backdropFilter: 'blur(4px)',
};

const cardStyle = {
  background: '#1e293b',
  borderRadius: 12,
  padding: '24px 28px',
  maxWidth: 560,
  width: '90%',
  border: '1px solid #334155',
  boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 14,
};

const labelStyle = {
  fontSize: 11,
  letterSpacing: 1.5,
  textTransform: 'uppercase',
  color: '#94a3b8',
};

const subLabelStyle = {
  fontSize: 10,
  letterSpacing: 1.2,
  textTransform: 'uppercase',
  color: '#64748b',
  marginBottom: 6,
};

const skipBtnStyle = {
  background: 'none',
  border: '1px solid #334155',
  borderRadius: 4,
  color: '#64748b',
  fontSize: 11,
  cursor: 'pointer',
  padding: '2px 8px',
};

const questionStyle = {
  fontSize: 17,
  fontWeight: 600,
  color: '#f8fafc',
  margin: '0 0 16px',
  lineHeight: 1.5,
};

const textareaStyle = {
  width: '100%',
  background: '#0f172a',
  border: '1px solid #334155',
  borderRadius: 8,
  color: '#e2e8f0',
  fontSize: 14,
  padding: '10px 12px',
  resize: 'vertical',
  outline: 'none',
  lineHeight: 1.6,
  boxSizing: 'border-box',
  marginBottom: 12,
  display: 'block',
};

const primaryBtnStyle = {
  width: '100%',
  padding: '10px 0',
  background: '#3b82f6',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

const predictionDisplayStyle = {
  background: '#0f172a',
  border: '1px solid #334155',
  borderRadius: 8,
  padding: '12px 14px',
  marginBottom: 12,
};

const answerDisplayStyle = {
  background: '#052e16',
  border: '1px solid #166534',
  borderRadius: 8,
  padding: '12px 14px',
  marginBottom: 16,
};
